import { ApolloError, gql, useQuery } from "@apollo/client";
import { DateTime } from "luxon";
import {
  Dispatch,
  SetStateAction,
  createContext,
  useCallback,
  useMemo,
  useState,
} from "react";
import { Group, Product } from "../types";

type Timeslot = { status: "AVAILABLE" | "NOT_AVAILABLE"; groups: Group[] };
type Availability = Record<string, Record<string, Timeslot>>;

type AvailabilityProviderProps = { productId: Product["id"] };

type AvailabilityContext = {
  availability: Availability;
  getTimeslots: (date: string) => Record<
    string,
    {
      status: "AVAILABLE" | "NOT_AVAILABLE";
      groups: Group[];
    }
  >;
  checkTimeslotIsAvailable: (
    date: string,
    time: string,
    numPeople: number,
    isPrivate: boolean
  ) => boolean;
  checkDateIsAvailable: (
    date: string,
    numPeople: number,
    isPrivate: boolean
  ) => boolean;
  getMaxSeatsLeft: (date: string, time: string) => number;
  loading: boolean;
  error: ApolloError | undefined;
  month: DateTime;
  setMonth: Dispatch<SetStateAction<DateTime>>;
};

export const AvailabilityContext =
  // @ts-expect-error context cannot take undefined
  createContext<AvailabilityContext>(undefined);

export default function AvailabilityProvider({
  productId,
  children,
}: React.PropsWithChildren<AvailabilityProviderProps>) {
  const [month, setMonth] = useState(
    DateTime.now().setZone("Asia/Tokyo").startOf("month")
  );

  const { data, loading, error } = useQuery<{ product: Product }>(
    GET_PRODUCT_AVAILABILITY,
    {
      variables: {
        productId: productId,
        fromDate: month.startOf("month").toFormat("yyyy-MM-dd"),
        untilDate: month.endOf("month").toFormat("yyyy-MM-dd"),
      },
      fetchPolicy: "no-cache",
    }
  );

  const availability = useMemo(
    () =>
      (data?.product.availability ?? []).reduce(
        (dateAcc, { date, timeSlots }) => {
          dateAcc[date] = timeSlots.reduce(
            (timeslotAcc, { time, status, groups }) => {
              timeslotAcc[time] = {
                status: status,
                groups: groups,
              };
              return timeslotAcc;
            },
            {} as Availability[string]
          );

          return dateAcc;
        },
        {} as Availability
      ),
    [data]
  );

  const checkGroupIsAvailable = useCallback(
    (group: Group, numPeople: number, isPrivate: boolean) =>
      !group.isPrivate &&
      numPeople <= group.seatsAvailable &&
      (!isPrivate || group.isEmpty),
    []
  );

  const getTimeslots = useCallback(
    (date: string) => availability[date] ?? {},
    [availability]
  );

  const getTimeslot = useCallback(
    (date: string, time: string) =>
      getTimeslots(date)[time] as Timeslot | undefined,
    [getTimeslots]
  );

  const getGroupsTimeslot = useCallback(
    (date: string, time: string) => getTimeslot(date, time)?.groups ?? [],
    [getTimeslot]
  );

  const getStatusTimeslot = useCallback(
    (date: string, time: string) => getTimeslot(date, time)?.status,
    [getTimeslot]
  );

  const getGroupsDate = useCallback(
    (date: string) =>
      Object.values(getTimeslots(date))
        .map((timeslot) => timeslot.groups)
        .flat(),
    [getTimeslots]
  );

  const checkTimeslotIsAvailable = useCallback(
    (date: string, time: string, numPeople: number, isPrivate: boolean) =>
      getStatusTimeslot(date, time) === "AVAILABLE" &&
      !!getGroupsTimeslot(date, time).find((group) =>
        checkGroupIsAvailable(group, numPeople, isPrivate)
      ),
    [getGroupsTimeslot, checkGroupIsAvailable]
  );

  const checkDateIsAvailable = useCallback(
    (date: string, numPeople: number, isPrivate: boolean) =>
      !!Object.keys(getTimeslots(date)).find((time) =>
        checkTimeslotIsAvailable(date, time, numPeople, isPrivate)
      ),
    [getGroupsDate, checkGroupIsAvailable]
  );

  const getMaxSeatsLeft = (date: string, time: string) =>
    getGroupsTimeslot(date, time).reduce(
      (acc, group) => Math.max(group.isPrivate ? 0 : group.seatsAvailable, acc),
      0
    );

  return (
    <AvailabilityContext.Provider
      value={{
        availability,
        getTimeslots,
        checkTimeslotIsAvailable,
        checkDateIsAvailable,
        getMaxSeatsLeft,
        loading,
        error,
        month,
        setMonth,
      }}
    >
      {children}
    </AvailabilityContext.Provider>
  );
}

const GET_PRODUCT_AVAILABILITY = gql`
  query GetProductAvailability(
    $productId: ID!
    $fromDate: Date!
    $untilDate: Date!
  ) {
    product(id: $productId) {
      availability(fromDate: $fromDate, untilDate: $untilDate) {
        date
        status
        timeSlots {
          time
          status
          groups {
            seatsAvailable
            isPrivate
            isEmpty
          }
        }
      }
    }
  }
`;
